import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { toast } from "react-hot-toast";
import { getDashboard, enrichAll } from "../api";

const COLORS = [
  "#f97316",
  "#a78bfa",
  "#60a5fa",
  "#4ade80",
  "#f87171",
  "#fbbf24",
  "#34d399",
  "#f472b6",
  "#818cf8",
  "#fb923c",
  "#22d3ee",
  "#a3e635",
  "#e879f9",
  "#94a3b8",
  "#fca5a5",
];

const SIGNED_COLORS = ["#a78bfa", "#444"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);

  const fetchData = () => {
    getDashboard()
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEnrichAll = async () => {
    setEnriching(true);
    try {
      const res = await enrichAll(100);
      const { enriched, not_found, failed, total_eligible } = res.data;
      toast.success(
        `Enriched ${enriched} products. ${not_found} not found, ${failed} failed. ${total_eligible - 100 > 0 ? `${total_eligible - 100} remaining.` : ""}`,
      );
      fetchData(); // Refresh stats
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Unknown error";
      toast.error("Enrichment failed: " + message);
    } finally {
      setEnriching(false);
    }
  };

  if (loading)
    return <p style={{ color: "#888", padding: 40 }}>Loading dashboard...</p>;
  if (!data)
    return (
      <p style={{ color: "#888", padding: 40 }}>
        Failed to load dashboard data.
      </p>
    );

  const {
    summary,
    genre_distribution,
    label_distribution,
    price_histogram,
    signed_breakdown,
    stock_by_region,
    availability,
    recently_added,
  } = data;
  const stockRegionData = [
    { name: "EU", count: stock_by_region.eu },
    { name: "GB", count: stock_by_region.gb },
    { name: "US", count: stock_by_region.us },
  ];

  const availabilityData = [
    { name: "In Stock", value: availability.in_stock },
    { name: "Out of Stock", value: availability.out_of_stock },
    { name: "Pre-Order", value: availability.pre_order },
  ];

  const AVAIL_COLORS = ["#4ade80", "#888", "#60a5fa"];

  return (
    <div className="dashboard-container">
      {/* Summary cards */}
      <div className="dashboard-summary">
        <div className="summary-card">
          <div className="card-value">{summary.total}</div>
          <div className="card-label">Total Products</div>
        </div>
        <div className="summary-card highlight">
          <div className="card-value">{summary.signed}</div>
          <div className="card-label">✍️ Signed</div>
        </div>
        <div className="summary-card">
          <div className="card-value">{summary.exclusive}</div>
          <div className="card-label">Exclusive</div>
        </div>
        <div className="summary-card">
          <div className="card-value">{summary.in_stock}</div>
          <div className="card-label">In Stock</div>
        </div>
        <div className="summary-card">
          <div className="card-value">{summary.pre_order}</div>
          <div className="card-label">Pre-Order</div>
        </div>
        <div className="summary-card">
          <div className="card-value">{summary.on_sale}</div>
          <div className="card-label">On Sale</div>
        </div>
        <div className="summary-card">
          <div className="card-value">€{summary.avg_price_eur?.toFixed(2)}</div>
          <div className="card-label">Avg Price</div>
        </div>
        {data.enrichment_stats && (
          <div className="summary-card" style={{ borderColor: "#3b3b5c" }}>
            <div className="card-value">
              {data.enrichment_stats.enriched}
              <span style={{ color: "#888", fontSize: "0.8rem" }}>
                {" "}
                /{" "}
                {data.enrichment_stats.enriched +
                  data.enrichment_stats.pending +
                  (data.enrichment_stats.not_found || 0)}
              </span>
            </div>
            <div className="card-label">💿 Discogs Enriched</div>
            {(data.enrichment_stats.not_found || 0) > 0 && (
              <div style={{ color: "#f59e0b", fontSize: "0.75rem" }}>
                {data.enrichment_stats.not_found} not found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="dashboard-charts">
        {/* Genre distribution */}
        <div className="chart-card">
          <h3>Genre Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={genre_distribution}
                dataKey="count"
                nameKey="genre"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ genre, count }) => `${genre} (${count})`}
                labelLine={false}
              >
                {genre_distribution.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Signed breakdown */}
        <div className="chart-card">
          <h3>Signed vs Unsigned</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={signed_breakdown}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                label={({ name, value }) => `${name}: ${value}`}
              >
                {signed_breakdown.map((_, i) => (
                  <Cell
                    key={i}
                    fill={SIGNED_COLORS[i % SIGNED_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Price histogram */}
        <div className="chart-card">
          <h3>Price Distribution (EUR)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={price_histogram}>
              <XAxis dataKey="range" tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis tick={{ fill: "#888", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: "#1a1a1a",
                  border: "1px solid #333",
                }}
              />
              <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Stock by region */}
        <div className="chart-card">
          <h3>In Stock by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stockRegionData}>
              <XAxis dataKey="name" tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis tick={{ fill: "#888", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: "#1a1a1a",
                  border: "1px solid #333",
                }}
              />
              <Bar dataKey="count" fill="#60a5fa" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Availability donut */}
        <div className="chart-card">
          <h3>Availability</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={availabilityData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                label={({ name, value }) => `${name}: ${value}`}
              >
                {availabilityData.map((_, i) => (
                  <Cell key={i} fill={AVAIL_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top labels */}
        <div className="chart-card">
          <h3>Top Labels</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={label_distribution} layout="vertical">
              <XAxis type="number" tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis
                type="category"
                dataKey="label_name"
                tick={{ fill: "#888", fontSize: 11 }}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  background: "#1a1a1a",
                  border: "1px solid #333",
                }}
              />
              <Bar dataKey="count" fill="#a78bfa" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recently added */}
        <div className="chart-card full-width">
          <h3>Recently Added</h3>
          <ol className="popular-list">
            {(recently_added || []).map((p, i) => (
              <li key={p.object_id} className="popular-item">
                <span className="popular-rank">{i + 1}.</span>
                <span className="popular-title">
                  {p.title}
                  {p.signed && (
                    <span
                      className="badge badge-signed"
                      style={{ marginLeft: 6 }}
                    >
                      ✍️
                    </span>
                  )}
                </span>
                <span className="popular-artist">{p.product_type}</span>
                <span className="popular-orders">
                  {p.created_at_shopify
                    ? new Date(p.created_at_shopify).toLocaleDateString()
                    : "—"}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="dashboard-footer">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {data.last_ingested && (
            <span>
              Last ingested: {new Date(data.last_ingested).toLocaleString()}
            </span>
          )}
          {!data.last_ingested && <span>No ingestion recorded yet</span>}
          {data.enrichment_stats?.pending > 0 && (
            <button
              className="btn btn-discogs"
              onClick={handleEnrichAll}
              disabled={enriching}
              style={{ padding: "6px 14px", fontSize: "0.85rem" }}
            >
              {enriching
                ? "Enriching..."
                : `💿 Enrich ${Math.min(100, data.enrichment_stats.pending)} Products`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
